'use client';

import { useState, useEffect } from 'react';
import PriceCard from './PriceCard';
import PriceChart from './PriceChart';
import PriceAlertModal from './PriceAlertModal';
import MarketFactorsPanel from './MarketFactorsPanel';
import ComparisonTool from './ComparisonTool';
import LocationPricing from './LocationPricing';
import Icon from '@/components/ui/AppIcon';

interface FishPrice {
  id: string;
  name: string;
  scientificName: string;
  image: string;
  alt: string;
  currentPrice: number;
  previousPrice: number;
  changePercent: number;
  trend: 'up' | 'down' | 'stable';
  availability: 'high' | 'medium' | 'low';
  unit: string;
  category: string;
}

interface ChartDataPoint {
  date: string;
  price: number;
}

interface MarketFactor {
  id: string;
  title: string;
  description: string;
  impact: 'positive' | 'negative' | 'neutral';
  icon: string;
}

interface FishOption {
  id: string;
  name: string;
  color: string;
}

interface LocationPrice {
  id: string;
  district: string;
  region: string;
  price: number;
  difference: number;
  explanation: string;
}

const FishPriceDiscoveryInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState<'name' | 'price' | 'change'>('name');
  const [selectedFish, setSelectedFish] = useState<FishPrice | null>(null);
  const [showChart, setShowChart] = useState(false);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [alertFish, setAlertFish] = useState<FishPrice | null>(null);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const fishPrices: FishPrice[] = [
  {
    id: '1',
    name: 'Salmon',
    scientificName: 'Salmo salar',
    image: "https://images.unsplash.com/photo-1706982272171-8ce965c1c996",
    alt: 'Fresh Atlantic salmon fillet with pink flesh on white background',
    currentPrice: 850.00,
    previousPrice: 820.00,
    changePercent: 3.7,
    trend: 'up',
    availability: 'high',
    unit: 'kg',
    category: 'premium'
  },
  {
    id: '2',
    name: 'Tuna',
    scientificName: 'Thunnus albacares',
    image: "https://images.unsplash.com/photo-1649730243029-13df7da2b843",
    alt: 'Yellowfin tuna steak with deep red flesh on ice',
    currentPrice: 1200.00,
    previousPrice: 1250.00,
    changePercent: -4.0,
    trend: 'down',
    availability: 'medium',
    unit: 'kg',
    category: 'premium'
  },
  {
    id: '3',
    name: 'Pomfret',
    scientificName: 'Pampus argenteus',
    image: "https://images.unsplash.com/photo-1495045877313-482119799e8e",
    alt: 'Silver pomfret fish with shiny scales on market display',
    currentPrice: 650.00,
    previousPrice: 650.00,
    changePercent: 0,
    trend: 'stable',
    availability: 'high',
    unit: 'kg',
    category: 'popular'
  },
  {
    id: '4',
    name: 'Prawns',
    scientificName: 'Penaeus monodon',
    image: "https://images.unsplash.com/flagged/photo-1568925320772-ce274118d46c",
    alt: 'Fresh tiger prawns with orange shells arranged on ice',
    currentPrice: 480.00,
    previousPrice: 450.00,
    changePercent: 6.7,
    trend: 'up',
    availability: 'high',
    unit: 'kg',
    category: 'popular'
  },
  {
    id: '5',
    name: 'Mackerel',
    scientificName: 'Scomber scombrus',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1d187a8ef-1766703908161.png",
    alt: 'Atlantic mackerel with distinctive striped pattern on white surface',
    currentPrice: 280.00,
    previousPrice: 300.00,
    changePercent: -6.7,
    trend: 'down',
    availability: 'high',
    unit: 'kg',
    category: 'budget'
  },
  {
    id: '6',
    name: 'Sardines',
    scientificName: 'Sardina pilchardus',
    image: "https://images.unsplash.com/photo-1684932581710-5e47833358d3",
    alt: 'Small silver sardines arranged in rows on market tray',
    currentPrice: 180.00,
    previousPrice: 175.00,
    changePercent: 2.9,
    trend: 'up',
    availability: 'high',
    unit: 'kg',
    category: 'budget'
  },
  {
    id: '7',
    name: 'Kingfish',
    scientificName: 'Scomberomorus commerson',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_17e1593f4-1766703912847.png",
    alt: 'Large kingfish steak with firm white flesh on cutting board',
    currentPrice: 550.00,
    previousPrice: 580.00,
    changePercent: -5.2,
    trend: 'down',
    availability: 'medium',
    unit: 'kg',
    category: 'popular'
  },
  {
    id: '8',
    name: 'Lobster',
    scientificName: 'Homarus gammarus',
    image: "https://images.unsplash.com/photo-1558991725-f16337342394",
    alt: 'Live European lobster with blue-black shell and large claws',
    currentPrice: 1800.00,
    previousPrice: 1650.00,
    changePercent: 9.1,
    trend: 'up',
    availability: 'low',
    unit: 'kg',
    category: 'premium'
  }];


  const categories = [
    { value: 'all', label: 'All Fish', icon: 'Squares2X2Icon' },
    { value: 'premium', label: 'Premium', icon: 'StarIcon' },
    { value: 'popular', label: 'Popular', icon: 'FireIcon' },
    { value: 'budget', label: 'Budget Friendly', icon: 'CurrencyRupeeIcon' },
  ];


  const marketFactors: MarketFactor[] = [
  {
    id: '1',
    title: 'Favorable Weather Conditions',
    description: 'Clear skies and calm seas have enabled increased fishing activities, leading to higher supply and stable prices for most varieties.',
    impact: 'positive',
    icon: 'SunIcon'
  },
  {
    id: '2',
    title: 'Seasonal Demand Surge',
    description: 'Festival season approaching has increased consumer demand, particularly for premium varieties like salmon and lobster.',
    impact: 'negative',
    icon: 'ArrowTrendingUpIcon'
  },
  {
    id: '3',
    title: 'Improved Cold Chain Logistics',
    description: 'Recent infrastructure upgrades have reduced wastage and transportation costs, benefiting overall price stability.',
    impact: 'positive',
    icon: 'TruckIcon'
  },
  {
    id: '4',
    title: 'Export Market Fluctuations',
    description: 'International demand for Indian seafood has affected domestic availability of certain premium varieties.',
    impact: 'neutral',
    icon: 'GlobeAltIcon'
  }];


  const availableFish: FishOption[] = [
  { id: '1', name: 'Salmon', color: '#C41E3A' },
  { id: '2', name: 'Tuna', color: '#2C5F7C' },
  { id: '3', name: 'Pomfret', color: '#FF6B35' },
  { id: '4', name: 'Prawns', color: '#38A169' },
  { id: '5', name: 'Mackerel', color: '#D69E2E' }];


  const generateChartData = (fishId: string): ChartDataPoint[] => {
    const days = 30;
    const data: ChartDataPoint[] = [];
    const basePrice = fishPrices.find((f) => f.id === fishId)?.currentPrice || 500;

    for (let i = days; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const variation = (Math.random() - 0.5) * 100;
      data.push({
        date: date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }),
        price: Math.max(basePrice + variation, basePrice * 0.8)
      });
    }
    return data;
  };

  const generateComparisonData = () => {
    const days = 30;
    const data: any[] = [];

    for (let i = days; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dataPoint: any = {
        date: date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })
      };

      availableFish.forEach((fish) => {
        const basePrice = fishPrices.find((f) => f.id === fish.id)?.currentPrice || 500;
        const variation = (Math.random() - 0.5) * 80;
        dataPoint[fish.id] = Math.max(basePrice + variation, basePrice * 0.85);
      });

      data.push(dataPoint);
    }
    return data;
  };

  const locationPrices: LocationPrice[] = [
  {
    id: '1',
    district: 'Mumbai Central',
    region: 'Maharashtra',
    price: 850.00,
    difference: 0,
    explanation: 'Base price at main distribution hub with direct access to fishing ports and optimal cold storage facilities.'
  },
  {
    id: '2',
    district: 'Pune',
    region: 'Maharashtra',
    price: 920.00,
    difference: 8.2,
    explanation: 'Slightly higher due to 200km transportation from coast and additional logistics costs for maintaining cold chain.'
  },
  {
    id: '3',
    district: 'Nashik',
    region: 'Maharashtra',
    price: 950.00,
    difference: 11.8,
    explanation: 'Premium pricing reflects longer transportation distance and lower local demand requiring specialized delivery.'
  },
  {
    id: '4',
    district: 'Thane',
    region: 'Maharashtra',
    price: 870.00,
    difference: 2.4,
    explanation: 'Proximity to Mumbai port keeps prices competitive with minimal additional transportation overhead.'
  }];


  const filteredFish = fishPrices.
  filter((fish) => {
    const matchesSearch = fish.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    fish.scientificName.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || fish.category === selectedCategory;
    return matchesSearch && matchesCategory;
  }).
  sort((a, b) => {
    switch (sortBy) {
      case 'price':
        return b.currentPrice - a.currentPrice;
      case 'change':
        return Math.abs(b.changePercent) - Math.abs(a.changePercent);
      default:
        return a.name.localeCompare(b.name);
    }
  });

  const handleViewDetails = (fishId: string) => {
    const fish = fishPrices.find((f) => f.id === fishId);
    if (fish) {
      setSelectedFish(fish);
      setShowChart(true);
    }
  };

  const handleSetAlert = (fishId: string) => {
    const fish = fishPrices.find((f) => f.id === fishId);
    if (fish) {
      setAlertFish(fish);
      setShowAlertModal(true);
    }
  };

  const handleAlertSubmit = (targetPrice: number, alertType: 'above' | 'below') => {
    console.log(`Alert set for ${alertFish?.name}: ${alertType} ₹${targetPrice}`);
  };

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-background pt-[60px]">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="animate-pulse space-y-6">
            <div className="h-12 bg-muted rounded-md w-1/3" />
            <div className="h-64 bg-muted rounded-lg" />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) =>
              <div key={i} className="h-96 bg-muted rounded-lg" />
              )}
            </div>
          </div>
        </div>
      </div>);

  }

  return (
    <div className="min-h-screen bg-background pt-[60px]">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex items-center justify-center w-12 h-12 bg-primary/10 rounded-lg">
              <Icon name="ChartBarIcon" size={28} className="text-primary" />
            </div>
            <div>
              <h1 className="font-heading font-bold text-3xl text-foreground">Fish Price Discovery</h1>
              <p className="text-muted-foreground">Real-time market prices with historical trends and insights</p>
            </div>
          </div>

          {/* Search and Filters */}
          <div className="flex flex-col lg:flex-row gap-4 mb-6">
            <div className="flex-1 relative">
              <Icon
                name="MagnifyingGlassIcon"
                size={20}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />

              <input
                type="text"
                placeholder="Search fish by name or scientific name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-card border border-border rounded-md text-foreground focus-ring" />

            </div>

            <div className="flex gap-3">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as any)}
                className="px-4 py-3 bg-card border border-border rounded-md text-foreground focus-ring">

                <option value="name">Sort by Name</option>
                <option value="price">Sort by Price</option>
                <option value="change">Sort by Change</option>
              </select>

              <button className="flex items-center gap-2 px-4 py-3 bg-primary text-primary-foreground rounded-md font-medium transition-smooth hover:shadow-elevation-md hover-lift">
                <Icon name="ArrowDownTrayIcon" size={20} />
                <span className="hidden md:inline">Export</span>
              </button>
            </div>
          </div>

          {/* Category Filters */}
          <div className="flex flex-wrap gap-3">
            {categories.map((category) =>
            <button
              key={category.value}
              onClick={() => setSelectedCategory(category.value)}
              className={`flex items-center gap-2 px-4 py-2 rounded-md font-medium text-sm transition-smooth ${
              selectedCategory === category.value ?
              'bg-primary text-primary-foreground shadow-elevation-sm' :
              'bg-card border border-border text-foreground hover:bg-muted'}`
              }>

                <Icon name={category.icon as any} size={18} />
                <span>{category.label}</span>
              </button>
            )}
          </div>
        </div>

        {/* Price Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-8">
          {filteredFish.map((fish) =>
          <PriceCard
            key={fish.id}
            fish={fish}
            onViewDetails={handleViewDetails}
            onSetAlert={handleSetAlert} />

          )}
        </div>

        {filteredFish.length === 0 &&
        <div className="text-center py-16">
            <Icon name="MagnifyingGlassIcon" size={64} className="mx-auto text-muted-foreground mb-4" />
            <h3 className="font-heading font-semibold text-xl text-foreground mb-2">No fish found</h3>
            <p className="text-muted-foreground">Try adjusting your search or filters</p>
          </div>
        }

        {/* Market Insights Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <MarketFactorsPanel factors={marketFactors} />
          <LocationPricing
            fishName={selectedFish?.name || 'Salmon'}
            locations={locationPrices} />

        </div>

        {/* Comparison Tool */}
        <ComparisonTool
          availableFish={availableFish}
          comparisonData={generateComparisonData()} />


        {/* Modals */}
        {showChart && selectedFish &&
        <PriceChart
          fishName={selectedFish.name}
          data={generateChartData(selectedFish.id)}
          onClose={() => setShowChart(false)} />

        }

        {showAlertModal && alertFish &&
        <PriceAlertModal
          fishName={alertFish.name}
          currentPrice={alertFish.currentPrice}
          onClose={() => setShowAlertModal(false)}
          onSetAlert={handleAlertSubmit} />

        }
      </div>
    </div>);

};

export default FishPriceDiscoveryInteractive;